import { useEffect, useState } from 'react'
import { Footer } from '@/components/Footer'
import { NavBar } from '@/components/nav-bar'
import { initializePaddle, Paddle } from '@paddle/paddle-js'
import useDarkMode from '@/hooks/useDarkMode'
import { SignIn, useUser } from '@clerk/clerk-react'
import { useAxios } from '@/contexts/AxiosProvider'
import { useRouter } from '@/hooks/useRouter'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { toast } from 'react-toastify'
import { PricingSection } from './(lobby)/PricingSection'
import { BillingCycle, PlanId } from '@db8/shared'
import { useAuth } from '@/contexts/AuthProvider'
import Loader from '@/components/loaders/Loader'
import TextLoading from '@/components/ui/textLoading'
import ReCAPTCHA from 'react-google-recaptcha'

// Resolve Paddle environment once and reuse
const paddleEnv = process.env.REACT_APP_PADDLE_ENV === 'production' ? 'production' : 'sandbox'
const isPaddleProd = paddleEnv === 'production'

const priceMap = {
  PRO: {
    monthly: isPaddleProd ? 'pri_01jwrzz4c1exet5yxrjby9zmma' : 'pri_01k52aacmpvqkd0y5qenn5w7tx',
  },
} as const

const priceIdToLevel = Object.entries(priceMap)
  .flatMap(([level, billing]) => Object.entries(billing).map(([cycle, id]) => [id, level]))
  .reduce(
    (acc, [id, level]) => {
      // @ts-ignore
      acc[id] = level
      return acc
    },
    {} as Record<string, keyof typeof priceMap>,
  )

export function PaddleCheckout() {
  const { queryParams, push } = useRouter()
  const [paddle, setPaddle] = useState<Paddle>()
  const [orgName, setOrgName] = useState('')
  const [selectedPlan, setSelectedPlan] = useState<PlanId>(
    (queryParams.plan?.toUpperCase() as PlanId) || 'PRO',
  )

  const [billingCycle, setBillingCycle] = useState<BillingCycle>(
    (queryParams.cycle?.toLowerCase() as BillingCycle) || 'monthly',
  )
  const [stage, setStage] = useState(0)
  const [captchaVerified, setCaptchaVerified] = useState(false)

  const isDark = useDarkMode()
  const { user } = useUser()
  const axios = useAxios()
  const { setActiveOrg, getCurrentUser } = useAuth()
  const selectedPriceId = priceMap[selectedPlan]?.[billingCycle] ?? null

  // logging removed

  useEffect(() => {
    if (!process.env.REACT_APP_PADDLE_TOKEN) {
      toast.error('Checkout setup error. Please configure Paddle token.')
    }
    const initCtx = {
      env: paddleEnv,
      selectedPlan,
      billingCycle,
      selectedPriceId,
      tokenPresent: Boolean(process.env.REACT_APP_PADDLE_TOKEN),
      origin: window.location.origin,
    }
    initializePaddle({
      environment: paddleEnv,
      token: process.env.REACT_APP_PADDLE_TOKEN,
      eventCallback: async (data) => {
        try {
          // no-op
        } catch {}
        if (data.name === 'checkout.error') {
          const reason = (data as any)?.data?.detail || (data as any)?.detail || 'unknown'
          // no-op
        }
        if (data.name === 'checkout.completed') {
          const transactionId = data.data.transaction_id
          const receivedPriceId = data.data.items?.[0]?.price_id
          const level = priceIdToLevel[receivedPriceId]
          const cycle = billingCycle.toUpperCase()
          setStage(3)

          const pollSubscription = async () => {
            try {
              const res = await axios.get(`/billing/transaction/${transactionId}`)
              const subscriptionId = res.data.transaction.subscription_id

              if (subscriptionId) {
                const org = await axios.post(`/orgs`, {
                  name: orgName,
                  subscriptionId,
                  level,
                  cycle,
                })
                setActiveOrg(org.data.id)
                getCurrentUser()
                push('/create-debate')
              } else {
                setTimeout(pollSubscription, 2000)
              }
            } catch (err) {
              // retry quietly
              setTimeout(pollSubscription, 2000)
            }
          }

          pollSubscription()
        }
      },
    }).then((p) => setPaddle(p))
  }, [orgName])

  useEffect(() => {
    if (stage === 2 && paddle && user && selectedPriceId) {
      paddle.Checkout.open({
        items: [{ priceId: (selectedPriceId || '').trim(), quantity: 1 }],
        settings: {
          displayMode: 'inline',
          frameTarget: 'paddle-container',
          frameInitialHeight: 600,
          frameStyle: 'width: 100%; min-width: 312px; background-color: transparent; border: none;',
          theme: isDark ? 'dark' : 'light',
        },
        customer: {
          email: user.primaryEmailAddress.emailAddress,
        },
      })
    }
  }, [stage, paddle, user, selectedPriceId])

  const verifyCaptcha = async (token: string) => {
    const res = await axios.post('/api/verify-captcha', { token })
    if (res.data.success) setCaptchaVerified(true)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (orgName.trim().length === 0) return
    if (!selectedPriceId) {
      toast.error('No price configured for the selected plan/cycle.')
      return
    }
    if (!captchaVerified) {
      toast.error('Please complete the CAPTCHA before continuing.')
      return
    }

    setStage(2)
  }

  return (
    <div className="overflow-hidden h-full flex flex-col min-h-screen">
      <NavBar />
      <div className="flex-grow flex items-center justify-center h-full">
        {!user ? (
          <SignIn forceRedirectUrl="/paddle-checkout" />
        ) : (
          <div className="flex flex-col h-full items-center w-full mx-auto px-4 py-10 space-y-10">
            {stage === 0 && (
              <div className="flex flex-col items-center w-full space-y-10">
                <PricingSection
                  selectedPlan={selectedPlan}
                  setSelectedPlan={setSelectedPlan}
                  billingCycle={billingCycle}
                  setBillingCycle={setBillingCycle}
                  buttonMode="select"
                />

                <Button
                  variant="secondary"
                  className="w-full max-w-4xl"
                  style={{ zIndex: 100 }}
                  onClick={() => setStage(1)}
                >
                  Confirm
                </Button>
              </div>
            )}
            {stage === 1 && (
              <form
                onSubmit={handleSubmit}
                className="w-full h-full max-w-3xl flex flex-col space-y-8 pt-4"
              >
                <div>
                  <Label htmlFor="orgName">Organization Name</Label>
                  <Input
                    id="orgName"
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    required
                    placeholder="Acme Inc."
                    maxLength={20}
                  />
                </div>

                <div className="flex justify-center">
                  {process.env.REACT_APP_RECAPTCHA_SITE_KEY ? (
                    <ReCAPTCHA
                      sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                      onChange={(token) => verifyCaptcha(token)}
                    />
                  ) : (
                    <div className="text-center p-4 border rounded">
                      <p className="text-sm text-gray-500">
                        reCAPTCHA not configured for local development
                      </p>
                      <button
                        type="button"
                        onClick={() => setCaptchaVerified(true)}
                        className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                      >
                        Skip for Development
                      </button>
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  variant="secondary"
                  className="w-full"
                  disabled={!captchaVerified}
                >
                  Confirm & Open Checkout
                </Button>
              </form>
            )}
            {stage === 2 && (
              <div
                id="paddle-container"
                className="paddle-container w-full mt-8 flex justify-center items-center"
              />
            )}

            {stage === 3 && (
              <div className="w-full h-full flex flex-col justify-center items-center">
                <Loader />
                <p className=" font-bold mt-24 text-center">
                  <TextLoading text="Creating Organization" />
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  )
}
